import { z } from 'zod';

import { cursorPaginationQuery } from './pagination.js';
import {
  VIOLATION_AGENCIES,
  VIOLATION_EVENT_KINDS,
  VIOLATION_SEVERITIES,
  VIOLATION_SOURCE_IDS,
  VIOLATION_STATUSES,
} from './violation-constants.js';

/**
 * Phase 7 — violation request/response schemas. Every endpoint that
 * accepts user input parses through one of these via the
 * `validate({ body | query | params })` middleware.
 *
 * Mirrors the conventions used by `compliance-schemas.ts`: trimmed
 * strings, csv-list filter coercion, optional/nullish handled with
 * `.transform((v) => v ?? null)` so callers always receive `null`
 * rather than `undefined`.
 */

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish()
    .transform((v) => v ?? null);

function csvList<T extends z.ZodTypeAny>(item: T) {
  return z
    .union([z.array(item), z.string()])
    .optional()
    .transform((v) => {
      if (v === undefined) return undefined;
      if (Array.isArray(v)) return v;
      const parts = v
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      return parts as unknown as z.infer<T>[];
    })
    .pipe(z.array(item).optional());
}

// ---------- list filters ----------

export const violationFiltersQuery = cursorPaginationQuery.extend({
  status: csvList(z.enum(VIOLATION_STATUSES)),
  severity: csvList(z.enum(VIOLATION_SEVERITIES)),
  agency: csvList(z.enum(VIOLATION_AGENCIES)),
  source: csvList(z.enum(VIOLATION_SOURCE_IDS)),
  buildingId: z.string().uuid().optional(),
  unitId: z.string().uuid().optional(),
  hasOpenWo: z.coerce.boolean().optional(),
  cureWithinDays: z.coerce.number().int().min(0).max(365).optional(),
  acknowledged: z.coerce.boolean().optional(),
  q: z.string().trim().max(200).optional(),
  sort: z
    .enum([
      'severity_issued',
      '-severity_issued',
      'issued_at',
      '-issued_at',
      'cure_by_at',
      '-cure_by_at',
      '-updated_at',
    ])
    .optional()
    .default('-severity_issued'),
});
export type ViolationFiltersQuery = z.infer<typeof violationFiltersQuery>;

// ---------- mutations ----------

export const acknowledgeViolationInput = z
  .object({
    note: optionalText(2_000).optional(),
  })
  .default({});
export type AcknowledgeViolationInput = z.infer<typeof acknowledgeViolationInput>;

export const dismissViolationInput = z.object({
  reason: z.string().trim().min(1).max(2_000),
});
export type DismissViolationInput = z.infer<typeof dismissViolationInput>;

/**
 * Operator reclassification of an `unknown`-status violation to `open`.
 * Used when a human eyeballs an agency row our adapters couldn't map and
 * decides it's a real, active violation. Optional note is recorded on the
 * event timeline.
 */
export const reopenViolationInput = z
  .object({
    note: optionalText(2_000).optional(),
  })
  .default({});
export type ReopenViolationInput = z.infer<typeof reopenViolationInput>;

export const linkViolationToWorkOrderInput = z.object({
  workOrderId: z.string().uuid(),
});
export type LinkViolationToWorkOrderInput = z.infer<typeof linkViolationToWorkOrderInput>;

/** Inputs for the "create work order from this violation" endpoint.
 *  All fields optional; the API fills in defaults from the violation
 *  payload (title from agency + summary, severity from violation,
 *  due-by from cure_by_at). */
export const createWorkOrderFromViolationInput = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    description: optionalText(8_000).optional(),
    severity: z.enum(['critical', 'high', 'medium', 'low']).optional(),
    dueAt: z.string().datetime({ offset: true }).nullish().transform((v) => v ?? null).optional(),
    assignedTeamId: z.string().uuid().nullish().transform((v) => v ?? null).optional(),
    assignedUserId: z.string().uuid().nullish().transform((v) => v ?? null).optional(),
    notes: optionalText(2_000).optional(),
  })
  .default({});
export type CreateWorkOrderFromViolationInput = z.infer<typeof createWorkOrderFromViolationInput>;

// ---------- bulk actions on the list page ----------

export const bulkAcknowledgeViolationsInput = z.object({
  violationIds: z.array(z.string().uuid()).min(1).max(200),
});
export type BulkAcknowledgeViolationsInput = z.infer<typeof bulkAcknowledgeViolationsInput>;

export const bulkCreateWorkOrdersFromViolationsInput = z.object({
  violationIds: z.array(z.string().uuid()).min(1).max(100),
  /** UI requires the caller to confirm the threshold by re-stating it.
   *  The API rejects entries below this severity. */
  minSeverity: z.enum(VIOLATION_SEVERITIES),
});
export type BulkCreateWorkOrdersFromViolationsInput = z.infer<
  typeof bulkCreateWorkOrdersFromViolationsInput
>;

// ---------- monitored sources ----------

export const monitoredSourceToggleInput = z.object({
  isEnabled: z.boolean(),
});
export type MonitoredSourceToggleInput = z.infer<typeof monitoredSourceToggleInput>;

export const sourceIdParam = z.object({
  sourceId: z.enum(VIOLATION_SOURCE_IDS),
});
export type SourceIdParam = z.infer<typeof sourceIdParam>;

// ---------- admin refresh ----------

export const refreshViolationInput = z
  .object({
    /** Force a fresh upstream call ignoring the polling cadence. */
    force: z.boolean().optional().default(true),
  })
  .default({});
export type RefreshViolationInput = z.infer<typeof refreshViolationInput>;

// ---------- normalized contract from adapters ----------

/**
 * The canonical contract every adapter normalizes to. Lives in
 * `@sitewise/shared` so the worker, scheduler, API, and tests share
 * one type. Optional fields are explicitly nullable rather than
 * `undefined` so the upsert/diff path doesn't have to handle two
 * "missing" representations.
 */
export const normalizedViolationSchema = z.object({
  sourceId: z.enum(VIOLATION_SOURCE_IDS),
  externalId: z.string().min(1),
  agency: z.enum(VIOLATION_AGENCIES),
  kind: z.string().min(1).max(120),
  severity: z.enum(VIOLATION_SEVERITIES),
  status: z.enum(VIOLATION_STATUSES),
  issuedAt: z.string().datetime({ offset: true }).nullable(),
  cureByAt: z.string().datetime({ offset: true }).nullable(),
  closedAt: z.string().datetime({ offset: true }).nullable(),
  summary: z.string().min(1).max(500),
  description: z.string().max(10_000).nullable(),
  locationText: z.string().max(500).nullable(),
  monetaryPenalty: z.number().nullable(),
  currency: z.string().length(3).default('USD'),
  externalUrl: z.string().url().nullable(),
  /** Building-scoping hints surfaced by the agency. The poll executor
   *  uses these to pick the right `building_id` when a single org has
   *  multiple buildings sharing a BIN/BBL. */
  bin: z.string().nullable(),
  bbl: z.string().nullable(),
  /** Optional pre-resolved unit identifier when the source includes
   *  one. The executor stores this as `unit_id` after matching against
   *  the org's `units` table. */
  unitHint: z.string().nullable(),
  raw: z.unknown(),
});
export type NormalizedViolation = z.infer<typeof normalizedViolationSchema>;

// ---------- list/event response shapes (server-authored) ----------

/**
 * Stable shape returned by the API for a violation row. Server-authored:
 * the API serializer parses against this on the way out, so adding a
 * column requires updating the parser explicitly. The web client uses
 * `z.infer<typeof violationResponse>` for its TanStack Query types.
 */
export const violationResponse = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  buildingId: z.string().uuid(),
  unitId: z.string().uuid().nullable(),
  sourceId: z.enum(VIOLATION_SOURCE_IDS),
  externalId: z.string(),
  agency: z.enum(VIOLATION_AGENCIES),
  kind: z.string(),
  severity: z.enum(VIOLATION_SEVERITIES),
  status: z.enum(VIOLATION_STATUSES),
  issuedAt: z.string().nullable(),
  cureByAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  summary: z.string(),
  description: z.string().nullable(),
  locationText: z.string().nullable(),
  monetaryPenalty: z.number().nullable(),
  currency: z.string(),
  externalUrl: z.string().nullable(),
  summaryAi: z.string().nullable(),
  autoWorkOrderId: z.string().uuid().nullable(),
  acknowledgedAt: z.string().nullable(),
  acknowledgedByUserId: z.string().uuid().nullable(),
  dismissedAt: z.string().nullable(),
  dismissedByUserId: z.string().uuid().nullable(),
  dismissReason: z.string().nullable(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Optional building/unit detail the list page renders inline. */
  buildingName: z.string().optional(),
  unitNumber: z.string().nullable().optional(),
});
export type ViolationResponse = z.infer<typeof violationResponse>;

export const violationEventResponse = z.object({
  id: z.string().uuid(),
  violationId: z.string().uuid(),
  kind: z.enum(VIOLATION_EVENT_KINDS),
  actorUserId: z.string().uuid().nullable(),
  actorKind: z.enum(['user', 'system', 'super_admin']),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
});
export type ViolationEventResponse = z.infer<typeof violationEventResponse>;

export const monitoredSourceResponse = z.object({
  id: z.string().uuid(),
  buildingId: z.string().uuid(),
  sourceId: z.enum(VIOLATION_SOURCE_IDS),
  isEnabled: z.boolean(),
  lastPolledAt: z.string().nullable(),
  lastSuccessAt: z.string().nullable(),
  lastFailureAt: z.string().nullable(),
  lastFailureReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MonitoredSourceResponse = z.infer<typeof monitoredSourceResponse>;

export const sourceHealthResponse = z.object({
  sourceId: z.enum(VIOLATION_SOURCE_IDS),
  consecutiveFailures: z.number().int().min(0),
  lastSuccessAt: z.string().nullable(),
  lastFailureAt: z.string().nullable(),
  lastFailureReason: z.string().nullable(),
  /** When set, the breaker is open until this instant. */
  cooldownUntil: z.string().nullable(),
  updatedAt: z.string(),
});
export type SourceHealthResponse = z.infer<typeof sourceHealthResponse>;

// ---------- dashboard rollup ----------

export const violationDashboardResponse = z.object({
  openByAgency: z.array(
    z.object({
      agency: z.enum(VIOLATION_AGENCIES),
      count: z.number().int().min(0),
    }),
  ),
  openBySeverity: z.array(
    z.object({
      severity: z.enum(VIOLATION_SEVERITIES),
      count: z.number().int().min(0),
    }),
  ),
  topSevereOpen: z.array(violationResponse),
  recentActivity: z.array(violationResponse),
  cureDeadlinesNext7d: z.array(violationResponse),
  /** Newly observed (non-complaint) violations in the last 7 days. */
  newLast7d: z.array(violationResponse),
  newLast7dCount: z.number().int().min(0),
  /** Newly observed 311 / HPD complaints in the last 7 days. */
  newComplaintsLast7d: z.array(violationResponse),
  newComplaintsLast7dCount: z.number().int().min(0),
});
export type ViolationDashboardResponse = z.infer<typeof violationDashboardResponse>;

/** Building rollup under a single agency board. */
export const violationAgencyBuildingRow = z.object({
  buildingId: z.string().uuid(),
  buildingName: z.string(),
  openCount: z.number().int().min(0),
  criticalCount: z.number().int().min(0),
  highCount: z.number().int().min(0),
});
export type ViolationAgencyBuildingRow = z.infer<typeof violationAgencyBuildingRow>;

export const violationAgencyBuildingsResponse = z.object({
  agency: z.enum(VIOLATION_AGENCIES),
  totalOpen: z.number().int().min(0),
  buildings: z.array(violationAgencyBuildingRow),
});
export type ViolationAgencyBuildingsResponse = z.infer<
  typeof violationAgencyBuildingsResponse
>;
